import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getInventoryAvailability } from "../../../../../../apis/inventory";
import RoomInventoryCalendar from "../../../../../../components/RoomInventoryCalendar";
import {
  CalendarIcon,
  RefreshIcon,
  ViewGridIcon,
  ViewListIcon,
  ChartBarIcon,
  SearchIcon,
} from "@heroicons/react/outline";

const Availability = () => {
  const { id: resortId } = useParams();
  const [viewType, setViewType] = useState("weekly");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);

  const fetchAvailability = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getInventoryAvailability(
        resortId,
        viewType,
        selectedDate
      );
      setAvailabilityData(response.data);
    } catch (error) {
      setError(error?.message || "Failed to fetch availability");
    } finally {
      setLoading(false);
    }
  }, [resortId, viewType, selectedDate]);

  return (
    <div className="availability-container">
      {/* Header Section */}
      <div className="availability-header">
        <div className="header-content">
          <div className="header-icon">
            <CalendarIcon className="w-6 h-6" />
          </div>
          <div>
            <h2 className="header-title">Room Availability</h2>
            <p className="header-subtitle">
              Manage and view room inventory across different time periods
            </p>
          </div>
        </div>

        {/* Stats Pills */}
        {availabilityData && (
          <div className="stats-pills">
            <div className="stat-pill stat-pill-blue">
              <ChartBarIcon className="w-4 h-4" />
              <span>Active View</span>
            </div>
          </div>
        )}
      </div>

      {/* Control Panel */}
      <div className="control-panel">
        <div className="controls-wrapper">
          {/* View Type Selector */}
          <div className="control-group">
            <label className="control-label">
              <ViewGridIcon className="w-4 h-4" />
              View Type
            </label>
            <div className="view-type-toggle">
              <button
                onClick={() => setViewType("weekly")}
                className={`toggle-option ${
                  viewType === "weekly" ? "active" : ""
                }`}
              >
                <ViewListIcon className="w-4 h-4" />
                <span>Weekly</span>
              </button>
              <button
                onClick={() => setViewType("monthly")}
                className={`toggle-option ${
                  viewType === "monthly" ? "active" : ""
                }`}
              >
                <ViewGridIcon className="w-4 h-4" />
                <span>Monthly</span>
              </button>
            </div>
          </div>

          {/* Date Picker */}
          <div className="control-group">
            <label className="control-label">
              <CalendarIcon className="w-4 h-4" />
              Start Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-input"
            />
          </div>

          {/* Fetch Button */}
          <div className="control-group">
            <label className="control-label invisible">Action</label>
            <button
              onClick={fetchAvailability}
              disabled={loading}
              className="fetch-button"
            >
              {loading ? (
                <>
                  <RefreshIcon className="w-5 h-5 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <SearchIcon className="w-5 h-5" />
                  <span>Get Availability</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p className="loading-text">Fetching availability data...</p>
          <p className="loading-subtext">This may take a few moments</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-state">
          <div className="error-icon">
            <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="error-title">Oops! Something went wrong</h3>
          <p className="error-message">{error}</p>
          <button onClick={fetchAvailability} className="retry-button">
            <RefreshIcon className="w-4 h-4" />
            <span>Try Again</span>
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && !availabilityData && (
        <div className="empty-state">
          <div className="empty-icon">
            <CalendarIcon className="w-20 h-20" />
          </div>
          <h3 className="empty-title">Room Availability Calendar</h3>
          <p className="empty-description">
            Select your preferred view type and start date above, then click "Get
            Availability" to view detailed room inventory data with real-time
            updates.
          </p>
          <div className="empty-features">
            <div className="feature-item">
              <div className="feature-icon">
                <ViewGridIcon className="w-5 h-5" />
              </div>
              <span>Multiple View Options</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <CalendarIcon className="w-5 h-5" />
              </div>
              <span>Date Range Selection</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <ChartBarIcon className="w-5 h-5" />
              </div>
              <span>Real-time Data</span>
            </div>
          </div>
          <div className="empty-cta">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
            <span>Configure parameters above to begin</span>
          </div>
        </div>
      )}

      {/* Calendar Display */}
      {availabilityData && !loading && (
        <div className="calendar-wrapper">
          <RoomInventoryCalendar availabilityData={availabilityData} />
        </div>
      )}

      {/* Styles */}
      <style jsx>{`
        .availability-container {
          padding: 0;
        }

        .availability-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 1rem;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 10px 25px -5px rgba(102, 126, 234, 0.3);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          width: 3.5rem;
          height: 3.5rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.3);
        }

        .header-title {
          font-size: 1.75rem;
          font-weight: 800;
          color: white;
          margin: 0 0 0.25rem 0;
        }

        .header-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0;
        }

        .stats-pills {
          display: flex;
          gap: 0.75rem;
        }

        .stat-pill {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 9999px;
          font-size: 0.875rem;
          font-weight: 600;
          border: 2px solid rgba(255, 255, 255, 0.3);
        }

        .stat-pill-blue {
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          color: white;
        }

        .control-panel {
          background: white;
          border-radius: 1rem;
          padding: 1.5rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .controls-wrapper {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          align-items: end;
        }

        .control-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .control-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .control-label.invisible {
          visibility: hidden;
        }

        .view-type-toggle {
          display: flex;
          background: #f3f4f6;
          border-radius: 0.75rem;
          padding: 0.25rem;
        }

        .toggle-option {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: transparent;
          border: none;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .toggle-option:hover {
          color: #374151;
        }

        .toggle-option.active {
          background: white;
          color: #8b5cf6;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .date-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: #f9fafb;
        }

        .date-input:focus {
          outline: none;
          border-color: #8b5cf6;
          background: white;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .fetch-button {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          border: none;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.3);
        }

        .fetch-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #7c3aed, #4f46e5);
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
        }

        .fetch-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .loading-state {
          background: white;
          border-radius: 1rem;
          padding: 4rem 2rem;
          text-align: center;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .loading-spinner {
          width: 3rem;
          height: 3rem;
          border: 4px solid #e5e7eb;
          border-top-color: #8b5cf6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1.5rem;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .loading-text {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .loading-subtext {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0;
        }

        .error-state {
          background: white;
          border-radius: 1rem;
          padding: 3rem 2rem;
          text-align: center;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 2px solid #fee2e2;
        }

        .error-icon {
          width: 5rem;
          height: 5rem;
          background: #fee2e2;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: #dc2626;
        }

        .error-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .error-message {
          font-size: 0.875rem;
          color: #dc2626;
          margin: 0 0 1.5rem 0;
        }

        .retry-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
          border: none;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .retry-button:hover {
          background: linear-gradient(135deg, #dc2626, #b91c1c);
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(239, 68, 68, 0.4);
        }

        .empty-state {
          background: white;
          border-radius: 1rem;
          padding: 4rem 2rem;
          text-align: center;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .empty-icon {
          width: 7rem;
          height: 7rem;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          color: #3b82f6;
          border: 3px solid #bfdbfe;
        }

        .empty-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 1rem 0;
        }

        .empty-description {
          font-size: 1rem;
          color: #6b7280;
          max-width: 600px;
          margin: 0 auto 2rem;
          line-height: 1.6;
        }

        .empty-features {
          display: flex;
          justify-content: center;
          gap: 2rem;
          flex-wrap: wrap;
          margin-bottom: 2rem;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.25rem;
          background: #f9fafb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
        }

        .feature-icon {
          width: 2rem;
          height: 2rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .empty-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border-radius: 0.75rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #3b82f6;
        }

        .calendar-wrapper {
          background: white;
          border-radius: 1rem;
          padding: 1.5rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        @media (max-width: 768px) {
          .availability-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .header-title {
            font-size: 1.5rem;
          }

          .controls-wrapper {
            grid-template-columns: 1fr;
          }

          .empty-features {
            flex-direction: column;
            gap: 1rem;
          }

          .feature-item {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Availability;